import { useMemo, useState } from "react";
import { BiSearchAlt } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IoSwapHorizontal } from "react-icons/io5";
import moment from "moment";
import BusSearchInput from "../../Components/Inputs/BusSearchInput";
import DatePicker from "../../util/DatePicker";

const BusGroup = ({ dark = false }: { dark?: boolean } = {}) => {
  const navigate = useNavigate();
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [searchAirports, setSearchAirports] = useState({
    from: null as any,
    to: null as any,
  });
  // Shares the flight tab's date picker (and therefore its redux state), so
  // the home page has one departure date regardless of the active tab.
  const formDate = useSelector((state: any) => state.form.date);
  const departureDate = formDate?.startDate
    ? moment.utc(formDate.startDate).format("YYYY-MM-DD")
    : "";

  const getSearchLocationValue = (airport: any, fallback: string) => {
    if (!airport) {
      return fallback.trim();
    }

    return (airport.name || airport.region || airport.code || fallback).trim();
  };

  const canSearch = useMemo(() => {
    const from = getSearchLocationValue(searchAirports.from, fromCity);
    const to = getSearchLocationValue(searchAirports.to, toCity);
    return from && to && departureDate;
  }, [searchAirports, fromCity, toCity, departureDate]);

  const searchInterstate = () => {
    if (!canSearch) {
      return;
    }

    const fromValue = getSearchLocationValue(searchAirports.from, fromCity);
    const toValue = getSearchLocationValue(searchAirports.to, toCity);

    const params = new URLSearchParams({
      from: fromValue,
      to: toValue,
      date: departureDate,
      adults: "1",
    });

    navigate(`/interstate?${params.toString()}`);
  };

  return (
    <div className="w-full">
      <div className="w-full flex items-center gap-2 mt-2">
        <div className="w-6/12 flex relative gap-2">
          <div className="w-1/2">
            <BusSearchInput
              name="From"
              placeholder="Leaving from"
              kind="from"
              dark={dark}
              value={searchAirports.from}
              initialSearch={fromCity}
              onChange={(option: any) => {
                setSearchAirports((prev) => ({ ...prev, from: option || null }));
                if (option?.name || option?.region || option?.code) {
                  setFromCity(option.name || option.region || option.code);
                } else if (!option) {
                  setFromCity("");
                }
              }}
            />
          </div>
          <div
            onClick={() =>
              (() => {
                setFromCity(toCity);
                setToCity(fromCity);
                setSearchAirports((prev) => ({
                  from: prev.to,
                  to: prev.from,
                }));
              })()
            }
            className={
              dark
                ? "absolute cursor-pointer z-[1] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500 shadow-[0_8px_24px_-6px_rgba(255,106,26,.75)] p-1.5"
                : "absolute cursor-pointer z-[1] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 border-white rounded-full bg-white"
            }
          >
            <div className={dark ? "" : "bg-orange-500 rounded-full p-1.5"}>
              <IoSwapHorizontal className="text-base text-white" />
            </div>
          </div>
          <div className="w-1/2">
            <BusSearchInput
              name="To"
              placeholder="Going to"
              kind="to"
              dark={dark}
              value={searchAirports.to}
              initialSearch={toCity}
              onChange={(option: any) => {
                setSearchAirports((prev) => ({ ...prev, to: option || null }));
                if (option?.name || option?.region || option?.code) {
                  setToCity(option.name || option.region || option.code);
                } else if (!option) {
                  setToCity("");
                }
              }}
            />
          </div>
        </div>

        <div className="w-3/12">
          <DatePicker dark={dark} />
        </div>

        <div className="w-3/12">
          <button
            type="button"
            onClick={searchInterstate}
            disabled={!canSearch}
            className="w-full h-14 bg-orange-500 text-white rounded flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-orange-400"
          >
            <BiSearchAlt />
            Search
          </button>
        </div>
      </div>
      <p className={dark ? "text-xs text-white/70 mt-3" : "text-xs text-slate-500 mt-3"}>
        Search interstate bus trips powered by our Interstate provider network.
      </p>
    </div>
  );
};

export default BusGroup;
